import React, { useState, useEffect, useCallback } from "react";

import { useParams, useHistory } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { get, cloneDeep } from "lodash";
import { CircularProgress, Box, Typography, Paper, Button, LinearProgress } from "@mui/material";
import { Download as DownloadIcon, Warning as WarningIcon } from "@mui/icons-material";
import { GET_DOCUMENT } from "./queries";
import { LAMBDA_S3_CONTENT_DISPOSITION_REWRITE } from "../../config";
import mime from "mime";
const Buffer = require("buffer/").Buffer;

function decodeAndValidateAppPath(base64: string): string | null {
  try {
    return Buffer.from(base64, "base64").toString("utf-8");
  } catch {
    return null;
  }
}

const DocumentViewer: React.FC = () => {
  const history = useHistory();
  const { id, backRef } = useParams<{ id: string; backRef: string }>();

  const backLink = decodeAndValidateAppPath(backRef);

  const { loading, error, data } = useQuery(GET_DOCUMENT, {
    variables: { docId: id },
  });

  const [file, setFile] = useState<any>(null);
  const [contentType, setContentType] = useState<string | null>("unknown");
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true);

  const _getBaseUrl = (url: string): string => {
    return url.split("?")[0];
  };

  const getContentType = useCallback((url: string): string => {
    const path = _getBaseUrl(url);
    return mime.getType(path) || "unknown";
  }, []);

  const getFileExtension = useCallback((url: string): string | null => {
    const path = _getBaseUrl(url);
    const parts = path.split(".");
    return parts.length > 1 ? parts.pop() || null : null;
  }, []);

  useEffect(() => {
    const document = cloneDeep(get(data, "documents.[0]", null));
    if (document) {
      const baseUrl = _getBaseUrl(document.source);
      document.sourceDownload = document.source;

      document.source = `${LAMBDA_S3_CONTENT_DISPOSITION_REWRITE}/?extension=${getFileExtension(
        baseUrl
      )}&url=${encodeURIComponent(document.source)}`;
      const contentType = getContentType(baseUrl);
      setContentType(contentType);

      setFile(document);
      if (contentType === "application/pdf" || contentType.startsWith("image/")) {
        setIsImageLoading(true);
      } else {
        setIsImageLoading(false);
      }
    }
  }, [data, getContentType, getFileExtension]);

  if (loading || !file) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        Loading ...
        <CircularProgress size={"5vw"} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography variant="h6" color="error" align="center">
          An error occurred while fetching the document: {error.message}
          <br />
          Please try refreshing the page or contact support if the issue persists.
        </Typography>
      </Box>
    );
  }

  if (!file) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography variant="h6" color="textSecondary" align="center">
          No document found. Please ensure the document URL is correct or contact support for assistance.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3} bgcolor="#f9f9f9" minHeight="100vh">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" color="primary">
          Document Viewer
        </Typography>
        <Box>
          {backLink && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push(backLink)}
              style={{ marginRight: "10px" }}>
              Go Back
            </Button>
          )}
          <Button variant="outlined" color="secondary" href={file.sourceDownload} download startIcon={<DownloadIcon />}>
            Download
          </Button>
        </Box>
      </Box>
      <Paper elevation={3} style={{ padding: "20px", borderRadius: "10px" }}>
        <Typography variant="h5" gutterBottom>
          Name: {file.docName}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Type: <strong>{file.docType}</strong>
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Created By: <strong>{file.createdBy}</strong>
        </Typography>
        {isImageLoading && <LinearProgress style={{ marginBottom: "10px" }} />}
        {contentType === "application/pdf" ? (
          <Box position="relative" display="flex" justifyContent="center" alignItems="center" mt={2}>
            <embed
              src={file.source}
              type="application/pdf"
              width="100%"
              height="600px"
              style={{ border: "1px solid #ddd", borderRadius: "5px" }}
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
            />
          </Box>
        ) : contentType?.startsWith("image/") ? (
          <Box position="relative" display="flex" justifyContent="center" alignItems="center" mt={2}>
            <img
              src={file.source}
              alt={file.docName}
              style={{
                width: "100%",
                margin: "0 auto",
                display: isImageLoading ? "none" : "block",
                borderRadius: "5px",
              }}
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
            />
          </Box>
        ) : (
          <Box textAlign="center" mt={4}>
            <WarningIcon color="error" style={{ fontSize: "50px" }} />
            <Typography variant="body1" color="textSecondary" style={{ marginTop: "10px" }}>
              Sorry, we cannot preview this type of document. Please use the download button above to access the file.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DocumentViewer;
